import { ref, computed, watch, shallowRef, onUnmounted, getCurrentInstance } from 'vue'
import { quote, quoteMany, type PriceRequest, type PriceResponse } from '../services/priceApi'

type Group = { id: string; isSize?: boolean; options?: any[]; selected?: any; title?: string }
type Cfg = { product: { id: number, basePrice?: number }, groups: Group[] }
type SizeOption = { id?: string; title: string; widthMm?: number; heightMm?: number; price?: number }

export function usePriceCalculator(opts: {
    cfg: () => Cfg | null
    quantity: () => number
    sizeOption?: () => SizeOption | null        // aktuell gewählte Größe (falls separat)
    categoryId?: () => number | null | undefined
    // optionally: current open group id to compute diffs for that one
    currentGroupId?: () => string | null
    // debounce ms
    debounceMs?: number
}) {
    const loading = ref(false)
    const error = ref<string | null>(null)
    const price = ref<number>(0)
    const currency = ref<string>('EUR')

    // Diffs pro Gruppe: { [groupId]: { [optionId]: number } } (Delta relativ zur aktuellen Auswahl)
    const diffsByGroup = shallowRef<Record<string, Record<string, number>>>({})
    // Diffs für Größen (falls Größe eine eigene Liste ist)
    const sizeDiffs = shallowRef<Record<string, number>>({})
    // Aufpreis je Gruppe für die aktuell gewählte Option (je Stück), z. B. { "2": 11.55 } für "4 cm"
    const groupTotals = shallowRef<Record<string, number>>({})

    // Cache, damit wir nicht dauernd dieselben Quotes holen
    // Bounded (~50 entries, oldest-first eviction) damit die Map nicht unbegrenzt wächst.
    const CACHE_MAX = 50
    const cache = new Map<string, PriceResponse>()
    const keyOf = (req: PriceRequest) => JSON.stringify(req)
    function cacheSet(k: string, res: PriceResponse) {
        // refresh insertion order so re-used keys count as most-recent
        if (cache.has(k)) cache.delete(k)
        cache.set(k, res)
        while (cache.size > CACHE_MAX) {
            const oldest = cache.keys().next().value
            if (oldest === undefined) break
            cache.delete(oldest)
        }
    }

    const fmtCurrency = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency.value || 'EUR' }).format(val)

    function buildRequest(): PriceRequest | null {
        const cfg = opts.cfg()
        if (!cfg) return null
        const variants = cfg.groups
            .filter(g => !g.isSize && g.selected)
            .map(g => ({ groupId: g.id, optionId: g.selected.id }))
        const sizeSel = opts.sizeOption?.()
        return {
            productId: Number(cfg.product.id),
            categoryId: opts.categoryId?.() ?? null,
            quantity: Math.max(Number(opts.quantity()), 1),
            size: sizeSel ? {
                id: sizeSel.id,
                widthMm: sizeSel.widthMm,
                heightMm: sizeSel.heightMm
            } : undefined,
            variants
        }
    }

    // Debounce
    let t: any
    function scheduleRefresh() {
        clearTimeout(t)
        t = setTimeout(refresh, opts.debounceMs ?? 150)
    }

    // Request-Sequencing: ein langsamer älterer quote() darf einen neueren nicht überschreiben.
    let requestSeq = 0

    async function refresh() {
        const req = buildRequest()
        if (!req) return
        const myReq = ++requestSeq
        error.value = null
        loading.value = true
        try {
            // Preis (gesamt)
            const k = keyOf(req)
            let res = cache.get(k)
            if (!res) {
                res = await quote(req)
                // Antwort verwerfen, falls inzwischen ein neuerer Request gestartet wurde.
                if (myReq !== requestSeq) return
                cacheSet(k, res)
            }
            if (myReq !== requestSeq) return
            price.value = res.price
            currency.value = res.currency || 'EUR'
            groupTotals.value = res.groupTotals && typeof res.groupTotals === 'object' ? res.groupTotals : {}

            // Diffs pro Variante ("+2,50 €" neben jeder nicht gewählten Option): ein quote-many-Aufruf
            // mit allen Alternativen aller Gruppen, Delta je Stück relativ zur aktuellen Auswahl.
            const cfg = opts.cfg()
            if (!cfg) return
            diffsByGroup.value = await computeVariantDiffs(req, cfg, res.price)
            if (myReq !== requestSeq) return

            // -- Größen-Diffs: Größen zeigen bereits ihren Stückpreis, daher keine Deltas
            sizeDiffs.value = {}
        } catch (e: any) {
            // Fehler eines veralteten Requests ignorieren.
            if (myReq !== requestSeq) return
            error.value = e?.message || 'Preisberechnung fehlgeschlagen'
        } finally {
            // loading nur löschen, wenn dies noch der neueste Request ist.
            if (myReq === requestSeq) loading.value = false
        }
    }

    // Mehr Alternativen als das pro Request anfragen wir nicht (Server rechnet jede einzeln).
    const DIFF_MAX_OVERRIDES = 80

    async function computeVariantDiffs(req: PriceRequest, cfg: Cfg, basePrice: number): Promise<Record<string, Record<string, number>>> {
        const groups = cfg.groups.filter(g => !g.isSize && g.selected && Array.isArray(g.options) && g.options.length > 1)
        const overrides: Array<{ key: string; variants: PriceRequest['variants'] }> = []
        for (const g of groups) {
            for (const o of g.options || []) {
                if (!o?.id || o.id === g.selected?.id) continue
                overrides.push({
                    key: `${g.id}:${o.id}`,
                    variants: req.variants.map(v => v.groupId === g.id ? { groupId: g.id, optionId: String(o.id) } : v),
                })
                if (overrides.length >= DIFF_MAX_OVERRIDES) break
            }
            if (overrides.length >= DIFF_MAX_OVERRIDES) break
        }
        if (!overrides.length) return {}

        const qty = Math.max(Number(req.quantity) || 1, 1)
        const k = `many:${keyOf(req)}`
        let many = cache.get(k) as unknown as Record<string, PriceResponse> | undefined
        if (!many) {
            try {
                many = await quoteMany(req, overrides)
            } catch {
                return {}
            }
            cacheSet(k, many as unknown as PriceResponse)
        }
        const diffs: Record<string, Record<string, number>> = {}
        for (const ov of overrides) {
            const r = many?.[ov.key]
            if (!r || typeof r.price !== 'number') continue
            const [groupId, optionId] = ov.key.split(':')
            const delta = Math.round(((r.price - basePrice) / qty) * 100) / 100
            ;(diffs[groupId] ||= {})[optionId] = delta
        }
        return diffs
    }

    function findSizeGroup(cfg: Cfg | null): Group | null {
        if (!cfg) return null
        return cfg.groups.find(g => g.isSize) || null
    }

    // Beobachte relevante Inputs.
    // Statt die gesamte cfg pro Tick zu stringifizieren, bilden wir eine schlanke
    // Signatur nur aus den preis-relevanten Feldern (genau die, die buildRequest()
    // an die API schickt): productId + alle nicht-Größen Varianten-Auswahlen.
    function priceSignature(): string {
        const cfg = opts.cfg?.()
        if (!cfg) return ''
        const variants = cfg.groups
            .filter(g => !g.isSize && g.selected)
            .map(g => `${g.id}:${g.selected?.id ?? ''}`)
            .join('|')
        return `${cfg.product?.id ?? ''}#${variants}`
    }
    function sizeSignature(): string {
        const s = opts.sizeOption?.()
        if (!s) return ''
        // nur die preis-relevanten Größen-Felder
        return `${s.id ?? ''}:${s.widthMm ?? ''}x${s.heightMm ?? ''}`
    }

    watch(
        [
            priceSignature,
            () => opts.quantity(),
            () => opts.categoryId?.() ?? null,
            sizeSignature,
            () => opts.currentGroupId?.() ?? null
        ],
        scheduleRefresh,
        { immediate: true }
    )

    // Debounce-Timer beim Unmount aufräumen (Composable kann auch außerhalb von
    // setup() aufgerufen werden, daher getCurrentInstance()-Guard).
    if (getCurrentInstance()) {
        onUnmounted(() => { if (t) clearTimeout(t) })
    }

    return {
        price, currency, loading, error,
        diffsByGroup,     // z.B. diffsByGroup[groupId][optionId] -> delta number
        sizeDiffs,        // z.B. sizeDiffs[sizeOptionId] -> delta number
        groupTotals,      // z.B. groupTotals[groupId] -> Aufpreis der gewählten Option je Stück
        fmtCurrency,
        refresh
    }
}
